import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { getRequest } from "@react/helpers/fetch"
import { t, hours, shortDate } from "../format"
import { GRID, Figures } from "./Figures"

const Avatar = ({ name, color }) => (
  <span className="h-7 w-7 shrink-0 rounded-full grid place-items-center text-[10.5px] font-semibold text-white"
        style={{ background: color || "#94a3b8" }}>
    {name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join("").toUpperCase()}
  </span>
)

const ProjectDetail = ({ project, currency }) => {
  const [detail, setDetail] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    getRequest(`/projects/hours_tracking/${project.id}.json`)
      .then(data => live && setDetail(data))
      .catch(() => live && setFailed(true))
    return () => { live = false }
  }, [project.id])

  if (failed) return <div className="px-12 pb-4 text-[12.5px] text-rose-600">{t("detail.failed")}</div>
  if (!detail) return <div className="px-12 pb-5 text-[12.5px] text-slate-400"><FontAwesomeIcon icon="circle-notch" spin className="mr-2" />{t("detail.loading")}</div>

  const weeks = detail.weeks || []
  return (
    <div className="pb-5">
      <div className="mx-5 mb-4 bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className={`${GRID} px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider font-medium text-slate-500`}>
          <div className="pl-[30px]">{t("columns.lot")}</div>
          <div className="text-right">{t("columns.sold")}</div>
          <div className="text-right">{t("columns.planned")}</div>
          <div className="text-right">{t("columns.done")}</div>
          <div>{t("columns.consumed_vs_progress")}</div>
          <div className="text-right">{t("columns.performance")}</div>
          <div className="text-right">{t("columns.projected")}</div>
          <div className="text-right">{t("columns.projected_variance")}</div>
          <div className="text-right">{t("columns.labor_cost")}</div>
          <div />
        </div>
        {detail.lots.map(lot => (
          <div key={lot.id ?? "none"} className={`${GRID} px-4 py-2.5 text-[12px] border-b border-slate-100 last:border-b-0`}>
            <div className="pl-[30px] min-w-0 truncate text-slate-800" title={lot.title}>
              {lot.title || <span className="italic text-slate-400">{t("no_lot")}</span>}
            </div>
            <Figures row={lot} currency={currency} compact />
            <div />
          </div>
        ))}
      </div>

      <div className="mx-5 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_260px] gap-4">
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-[minmax(160px,1.4fr)_64px_64px_64px_repeat(8,minmax(38px,1fr))_72px] gap-2 px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10.5px] uppercase tracking-wider font-medium text-slate-500">
            <div>{t("detail.team", { count: detail.workers.length })}</div>
            <div className="text-right">{t("columns.planned")}</div>
            <div className="text-right">{t("columns.done")}</div>
            <div className="text-right">{t("columns.approved")}</div>
            {weeks.map(week => <div key={week} className="text-right normal-case tracking-normal">{shortDate(week)}</div>)}
            <div className="text-right">{t("detail.last_day")}</div>
          </div>
          {detail.workers.length === 0 ? (
            <div className="px-4 py-6 text-center text-[12.5px] text-slate-400">{t("detail.no_team")}</div>
          ) : detail.workers.map(worker => (
            <div key={worker.id} className="grid grid-cols-[minmax(160px,1.4fr)_64px_64px_64px_repeat(8,minmax(38px,1fr))_72px] gap-2 px-4 py-2 items-center text-[12px] border-b border-slate-100 last:border-b-0">
              <div className="flex items-center gap-2 min-w-0">
                <Avatar name={worker.name} color={worker.color} />
                <span className="truncate text-slate-800">{worker.name}</span>
              </div>
              <div className="text-right tabular-nums text-sky-700">{hours(worker.planned)}</div>
              <div className="text-right tabular-nums font-medium text-slate-900">{hours(worker.done)}</div>
              <div className={`text-right tabular-nums ${worker.approved < worker.done ? "text-amber-700" : "text-slate-500"}`}>{hours(worker.approved)}</div>
              {worker.trend.map((value, index) => (
                <div key={index} className={`text-right tabular-nums ${value > 0 ? "text-slate-700" : "text-slate-300"}`}>
                  {value > 0 ? String(value).replace(".", ",") : "·"}
                </div>
              ))}
              <div className="text-right text-slate-500">{shortDate(worker.last_day) || "–"}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <a href={project.path} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-[12.5px] text-slate-700 hover:bg-slate-50">
            <FontAwesomeIcon icon="table-columns" className="w-3.5 text-slate-400" />{t("detail.open_project")}
          </a>
          <a href={`/projects/planning?project_id=${project.id}`} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-[12.5px] text-slate-700 hover:bg-slate-50">
            <FontAwesomeIcon icon="calendar-days" className="w-3.5 text-slate-400" />{t("detail.open_planning")}
          </a>
          <a href={`/projects/hours?project_id=${project.id}`} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-[12.5px] text-slate-700 hover:bg-slate-50">
            <FontAwesomeIcon icon="stamp" className="w-3.5 text-slate-400" />
            {project.pending > 0 ? t("detail.approve", { hours: hours(project.pending) }) : t("detail.open_hours")}
          </a>
          {!project.progress_known && (
            <p className="mt-1 px-1 text-[11.5px] leading-relaxed text-slate-500">
              <FontAwesomeIcon icon="circle-info" className="mr-1.5 text-slate-400" />{t("detail.progress_hint")}
            </p>
          )}
          {project.unplanned > 0 && (
            <p className="px-1 text-[11.5px] leading-relaxed text-amber-700">
              <FontAwesomeIcon icon="triangle-exclamation" className="mr-1.5" />{t("detail.unplanned", { hours: hours(project.unplanned) })}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProjectDetail
